// Plain loose-FK edge helpers for the IntelliSense loose-FK JOIN emitter
// (`looseFkJoinSnippet.ts`), split out for the 200-line cap: the declaration
// as an `FkEdge`, its columns in catalog spelling, and the mirror test
// against the snapshot's real FKs.

import type { DatabaseEngineId, FkEdge } from '../../../core/engines';
import { databaseEngineIdToSqlEngine } from '../../../core/engines/databaseEngineIdToSqlEngine';
import type { LooseRelationship } from '@/types/electron';
import { declaredCatalogSpelling } from '../../sql-resolver/suggest/sqlIdentifierText';

import { schemaMatches } from './fkJoinSnippet';
import type { FkEndpointMatcher } from './fkEndpointMatch';
import type { FilterColumnType } from './looseFkJoinFilters';

/**
 * Convert a *plain* loose relationship to a single-column `FkEdge` so it can
 * flow through the same FK-join path as real constraints. Returns `null` for
 * JSON-path rels (`sourcePath` set) — those need the dedicated emitter below
 * because a JSON-extract expression cannot be a bare `FkEdge` column.
 */
export function looseRelToFkEdge(rel: LooseRelationship): FkEdge | null {
  if (rel.sourcePath) return null;
  const edge: FkEdge = {
    fromTable: rel.fromTable,
    fromColumns: [rel.fromColumn],
    toTable: rel.toTable,
    toColumns: [rel.toColumn],
    name: rel.id,
  };
  return rel.fromSchema || rel.toSchema
    ? {
        ...edge,
        ...(rel.fromSchema ? { fromSchema: rel.fromSchema } : {}),
        ...(rel.toSchema ? { toSchema: rel.toSchema } : {}),
      }
    : edge;
}

/**
 * `fk` (a plain loose rel's single-column edge) with each declared column in
 * the catalog spelling of the table it lives on (`declaredCatalogSpelling`):
 * the source column among `sourceColumns`, the target among `targetColumns`.
 */
export function withCatalogColumns(
  fk: FkEdge,
  sourceColumns: readonly FilterColumnType[] | undefined,
  targetColumns: readonly FilterColumnType[] | undefined,
  engine: DatabaseEngineId,
): FkEdge {
  const sqlEngine = databaseEngineIdToSqlEngine(engine);
  if (!sqlEngine) return fk;
  const spell = (declared: string, columns: readonly FilterColumnType[] | undefined) =>
    declaredCatalogSpelling(declared, columns?.map((c) => c.name));
  return {
    ...fk,
    fromColumns: fk.fromColumns.map((c) => spell(c, sourceColumns)),
    toColumns: fk.toColumns.map((c) => spell(c, targetColumns)),
  };
}

/**
 * A plain loose rel "mirrors" a real FK when a real single-column FK edge
 * connects the same from-table.from-column → to-table.to-column (columns
 * compared case-insensitively). Such loose rels are suppressed so the user
 * does not see a duplicate JOIN candidate. The tables are the same when their
 * endpoints name a common snapshot table (`match`, relation identity: a real
 * FK on `orders` never hides a loose rel on `"Orders"`); without `match`,
 * case-insensitive names with any-schema semantics.
 */
export function edgeMirrorsRealFk(
  realFks: readonly FkEdge[],
  edge: FkEdge,
  match: FkEndpointMatcher | undefined,
): boolean {
  const sameTable = (
    aSchema: string | undefined,
    aName: string,
    bSchema: string | undefined,
    bName: string,
  ): boolean =>
    match
      ? match.endpointTables(aSchema, aName).some((t) => match.endpointIs(bSchema, bName, t))
      : aName.toLowerCase() === bName.toLowerCase() &&
        // Same tables means same schemas too: a real FK between one schema's
        // tables must not hide a loose rel between another's same-named ones.
        schemaMatches(aSchema, bSchema);
  return realFks.some(
    (fk) =>
      fk.fromColumns.length === 1 &&
      fk.toColumns.length === 1 &&
      sameTable(fk.fromSchema, fk.fromTable, edge.fromSchema, edge.fromTable) &&
      sameTable(fk.toSchema, fk.toTable, edge.toSchema, edge.toTable) &&
      fk.fromColumns[0]?.toLowerCase() === edge.fromColumns[0]?.toLowerCase() &&
      fk.toColumns[0]?.toLowerCase() === edge.toColumns[0]?.toLowerCase(),
  );
}
